import { ReactNode } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc';
export type CellAlign = 'left' | 'center' | 'right';

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  /** Gdy nie podany, renderujemy row[key] jako string. */
  render?: (row: T, rowIndex: number) => ReactNode;
  align?: CellAlign;
  /** CSS width (np. '120px', '20%'). */
  width?: string;
  sortable?: boolean;
  /** Klasa na komórkę - dla np. font-mono w kolumnach liczbowych. */
  cellClassName?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  /** Wymagane — React key dla każdego wiersza. */
  getRowKey: (row: T, index: number) => string;
  onRowClick?: (row: T, index: number) => void;
  striped?: boolean;
  compact?: boolean;
  /** Controlled sort. onSort wywoływany z nowym stanem. */
  sortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (key: string, direction: SortDirection) => void;
  loading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: ReactNode;
  className?: string;
}

const ALIGN_CLASS: Record<CellAlign, string> = {
  left:   'text-left',
  center: 'text-center',
  right:  'text-right',
};

/**
 * Prosta, generyczna tabela. Bez wirtualizacji - dla >100 rows używaj
 * react-virtuoso (już w deps projektu). Sort controlled - rodzic zarządza
 * stanem i sortuje dane (lub trzyma to w URL / API query).
 */
export function Table<T>({
  columns,
  data,
  getRowKey,
  onRowClick,
  striped = false,
  compact = false,
  sortKey,
  sortDirection,
  onSort,
  loading = false,
  loadingRowCount = 5,
  emptyMessage = 'Brak danych',
  className,
}: TableProps<T>) {
  const cellPadding = compact ? 'px-3 py-1.5' : 'px-4 py-3';
  const headPadding = compact ? 'px-3 py-2' : 'px-4 py-2.5';

  const handleSortClick = (col: TableColumn<T>) => {
    if (!col.sortable || !onSort) return;
    const nextDir: SortDirection = sortKey === col.key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSort(col.key, nextDir);
  };

  return (
    <div className={['rounded-xl border border-border-subtle bg-surface-raised/30 overflow-hidden', className].filter(Boolean).join(' ')}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-surface-raised/60 border-b border-border-subtle">
              {columns.map(col => {
                const isActive = sortKey === col.key;
                const SortIcon = !col.sortable ? null : !isActive ? ArrowUpDown : sortDirection === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={col.key}
                    style={col.width ? { width: col.width } : undefined}
                    className={[
                      headPadding,
                      ALIGN_CLASS[col.align ?? 'left'],
                      'text-xxs font-semibold uppercase tracking-wider text-text-muted',
                      col.sortable ? 'cursor-pointer select-none hover:text-text-primary transition-colors' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSortClick(col)}
                    aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.header}
                      {SortIcon && <SortIcon className={`w-3 h-3 ${isActive ? 'text-accent-primary' : 'text-text-disabled'}`} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: loadingRowCount }).map((_, i) => (
                <tr key={`skel-${i}`} className="border-b border-border-subtle last:border-0">
                  {columns.map(col => (
                    <td key={col.key} className={cellPadding}>
                      <div className="h-3.5 bg-surface-hover/60 rounded animate-pulse-slow" style={{ width: `${60 + (i * 7) % 40}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-text-muted">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => (
                <tr
                  key={getRowKey(row, rowIdx)}
                  onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
                  className={[
                    'border-b border-border-subtle last:border-0 transition-colors',
                    striped && rowIdx % 2 === 1 ? 'bg-surface-raised/20' : '',
                    onRowClick ? 'cursor-pointer hover:bg-surface-hover/40' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {columns.map(col => (
                    <td
                      key={col.key}
                      className={[
                        cellPadding,
                        ALIGN_CLASS[col.align ?? 'left'],
                        'text-sm text-text-secondary',
                        col.cellClassName,
                      ].filter(Boolean).join(' ')}
                    >
                      {col.render ? col.render(row, rowIdx) : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
